import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  ListGroup,
  ListGroupItem,
  Container,
  DropdownItem,
  Spinner,
  Button
} from "reactstrap";
import { Badge } from "@mui/material";
import SimpleHeader from "components/Headers/SimpleHeader";
import { useAuth } from "context/AuthContext";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";
import Pagination from "views/pages/components/Pagination";
import DropdownFilter from "utils/DropdownFilter";

const Messages = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [hoveredConversationId, setHoveredConversationId] = useState(null);
  const [messageTypeFilter, setMessageTypeFilter] = useState(
    helpers.NOTIFICATION_FILTERS.ALL
  );
  const handleClearFilters = () => {
    setMessageTypeFilter(helpers.NOTIFICATION_FILTERS.ALL);
    setCurrentPage(0);
  };

  const fetchConversations = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await userServices.fetchOrderConversation({
        conversation_type: "all",
        page: currentPage + 1,
        limit: rowsPerPage,
        filterType: messageTypeFilter !== helpers.NOTIFICATION_FILTERS.ALL ? messageTypeFilter : undefined
      });
      const dataObj = response?.data || response;
      setConversations(Array.isArray(dataObj?.conversations) ? dataObj.conversations : []);
      setTotalRows(dataObj?.total || 0);
    } catch (error) {
      console.error("Error fetching conversations:", error);
      setConversations([]);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, messageTypeFilter, navigate]);

  useEffect(() => {
    if (isAuthenticated) fetchConversations();
    else navigate("/login");
  }, [isAuthenticated, fetchConversations, navigate]);

  const markConversationAsRead = async (conversationId, type, channel) => {
    const payload = {
      type: "markAllAsRead",
      conversationId,
      conversationType: type,
      channel,
    };
    try {
      await userServices.updateConversationMessage(payload);
      setConversations((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.reference_id === conversationId
            ? { ...conversation, unreadCount: 0 }
            : conversation
        )
      );
    } catch (error) {
      console.error("Error marking conversation as read:", error);
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner />
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SimpleHeader
        name={t("page.messages.title", "Conversations")}
        parentName={t("sideNav.messages", "Messages")}
      />

      <Container className="mt--6" fluid>
        <Card>
          <CardHeader className="d-flex justify-content-between flex-column flex-md-row">

            <h5 className="h3 mb-0 d-flex align-items-center">
              {t("page.messages.title", "Conversations")}
            </h5>

            <div className="listing-header-right d-flex align-items-center">
              <span className="text-sm font-weight-600 text-muted mr-2">
                {t("table.filterBy", "Filter By")}
              </span>

              <DropdownFilter
                value={messageTypeFilter}
                options={helpers.getFilter("MESSAGE_TYPE", t)}
                onChange={(value) => {
                  setMessageTypeFilter(value);
                  setCurrentPage(0);
                }}
                buttonClass="btn-neutral filter-select2"
              />
              <Button
                size="sm"
                className="ml-2 btn-neutral text-danger font-weight-600"
                onClick={handleClearFilters}
              >
                {t("common.clearFilters", "Clear Filters")}
              </Button>
            </div>

          </CardHeader>

          <CardBody className="p-0">
            {conversations.length > 0 ? (
              <ListGroup flush>
                {conversations.map((conversation) => {
                  const isRead = conversation.unreadCount === 0;

                  return (
                    <ListGroupItem
                      key={conversation.reference_id}
                      tag="div"
                      onMouseEnter={() =>
                        setHoveredConversationId(conversation.reference_id)
                      }
                      onMouseLeave={() => setHoveredConversationId(null)}
                      className={`list-group-item-action py-4 px-4 position-relative ${!isRead ? "bg-blue-50" : "bg-white"
                        } hover:bg-blue-100 transition`}
                      onClick={() =>
                        helpers.handleOpenConversation({
                          conversation,
                          navigate,
                          markAsRead: markConversationAsRead
                        })
                      }
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2 overflow-hidden">
                          <div className="flex-shrink-0 mr-4">
                            {helpers.getMessageIcon({
                              type: conversation.context_type,
                            })}
                          </div>

                          <div className="flex flex-col truncate">
                            <h5
                              className={`mb-0 truncate ${!isRead
                                  ? "font-bold text-[#111]"
                                  : "text-gray-800"
                                }`}
                            >
                              {conversation.title || t("page.messages.untitled", "Untitled conversation")}
                            </h5>

                            <small className="subtitle-small truncate">
                              {conversation.lastMessageKey
                                ? t(conversation.lastMessageKey)
                                : conversation.lastMessage
                                  ? conversation.lastMessage.slice(0, 80)
                                  : t("page.messages.noMessagesYet", "No messages yet")}
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {!isRead &&
                            hoveredConversationId === conversation.reference_id && (
                              <button
                                className="btn btn-sm btn-outline-primary mr-4"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markConversationAsRead(
                                    conversation.reference_id,
                                    conversation.context_type,
                                    conversation.channel
                                  );
                                }}
                              >
                                {t("page.messages.markAsRead", "Mark all as read")}
                              </button>
                            )}
                          {!isRead && (
                            <Badge
                              badgeContent={conversation.unreadCount}
                              className="custom-badge mr-3"
                            />
                          )}
                          <small className="ml-1">
                            {helpers.getRelativeTimeForMessages(conversation.lastMessageAt || conversation.updatedAt, t)}
                          </small>
                        </div>
                      </div>
                    </ListGroupItem>
                  );
                })}
              </ListGroup>
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center py-6">
                <span className="text-sm md:text-base">
                  {t("page.messages.noConversation", "No Conversations Yet")}
                </span>
              </div>
            )}

            <DropdownItem divider />

            <Pagination
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              totalRows={totalRows}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(size) => {
                setRowsPerPage(size);
                setCurrentPage(0);
              }}
              t={t}
            />
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default Messages;
